'use client';

import clsx from 'clsx';

import { Icon, type IconName } from '@/components/ui/Icon';
import { Link, useRouter } from '@/lib/i18n/navigation';

import styles from './TrapezoidTabs.module.scss';

export interface TrapezoidTab {
  id: string;
  label: string;
  icon?: IconName;
  activeIcon?: IconName;
  href?: string;
}

export interface TrapezoidTabsProps {
  tabs: TrapezoidTab[];
  activeTabId: string;
  onTabChange?: (tabId: string) => void;
  className?: string;
  ariaLabel?: string;
}

function TabItem({
  tab,
  isActive,
  onTabChange,
}: {
  tab: TrapezoidTab;
  isActive: boolean;
  onTabChange?: (tabId: string) => void;
}) {
  const iconName = isActive ? tab.activeIcon || tab.icon : tab.icon;

  const handleClick = () => {
    onTabChange?.(tab.id);
  };

  const content = (
    <span className={styles.tabContent}>
      {iconName && (
        <span className={styles.iconWrapper}>
          <Icon name={iconName} size={20} aria-hidden="true" />
        </span>
      )}
      <span className={styles.tabLabel}>{tab.label}</span>
    </span>
  );

  const tabClassName = clsx(styles.tab, isActive && styles.active);

  if (tab.href) {
    return (
      <Link
        href={tab.href}
        className={tabClassName}
        aria-current={isActive ? 'page' : undefined}
        onClick={handleClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      role="tab"
      onClick={handleClick}
      className={tabClassName}
      aria-selected={isActive}
    >
      {content}
    </button>
  );
}

export function TrapezoidTabs({
  tabs,
  activeTabId,
  onTabChange,
  className,
  ariaLabel = 'Tabs',
}: TrapezoidTabsProps) {
  const router = useRouter();
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
  const isLinkBased = tabs.some((tab) => tab.href);

  const handleTabClick = (tabId: string) => {
    onTabChange?.(tabId);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selectedTab = tabs.find((t) => t.id === selectedId);

    if (selectedTab?.href) {
      handleTabClick(selectedId);
      router.push(selectedTab.href);
    } else {
      handleTabClick(selectedId);
    }
  };

  const tabItems = tabs.map((tab) => (
    <TabItem
      key={tab.id}
      tab={tab}
      isActive={tab.id === activeTabId}
      onTabChange={handleTabClick}
    />
  ));

  const DesktopWrapper = isLinkBased ? 'nav' : 'div';
  const desktopProps = isLinkBased
    ? { 'aria-label': ariaLabel }
    : { role: 'tablist' as const, 'aria-label': ariaLabel };

  return (
    <div className={className}>
      {/* Mobile: dropdown selector */}
      <div className={styles.mobileDropdown}>
        {(activeTab?.activeIcon || activeTab?.icon) && (
          <Icon
            name={(activeTab.activeIcon || activeTab.icon) as IconName}
            size={18}
            aria-hidden="true"
            className={styles.dropdownIcon}
          />
        )}
        <select
          className={styles.mobileSelect}
          value={activeTabId}
          onChange={handleSelectChange}
          aria-label={`Navigate ${ariaLabel}`}
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {tab.label}
            </option>
          ))}
        </select>
        <Icon name="chevron-down" size={16} aria-hidden="true" className={styles.dropdownChevron} />
      </div>

      {/* Desktop: navigation links or stateful tab buttons */}
      <DesktopWrapper className={styles.tabs} {...desktopProps}>
        {tabItems}
      </DesktopWrapper>
    </div>
  );
}

TrapezoidTabs.displayName = 'TrapezoidTabs';
